import { apiClient } from '../client';
import { API_ENDPOINTS } from '../config';

export interface Message {
  id: string;
  conversation_id: string;
  tenant_id: string | null;
  sender_phone: string;
  recipient_phone: string;
  message_type: string;
  content_text: string | null;
  media_url: string | null;
  media_type: string | null;
  is_inbound: boolean;
  status: string;
  whatsapp_message_id: string | null;
  template_id: string | null;
  created_at: string;
  delivered_at: string | null;
  read_at: string | null;
  metadata?: any;
}

export interface CreateMessageData {
  conversationId: string;
  content: string;
  messageType?: string;
  direction?: string;
  status?: string;
  whatsappMessageId?: string;
  metadata?: object;
}

export interface MessageFilters {
  conversationId?: string;
  businessAccountId?: string;
  contactId?: string;
  messageType?: string;
  status?: string;
  isInbound?: boolean;
  readAt?: string | null; // null means unread messages (maps to read_at in backend)
  search?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  ascending?: boolean;
  pageToken?: string;
}

export const messagesApi = {
  /**
   * Get all messages with optional filtering
   */
  async getAll(filters: MessageFilters = {}) {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        // Map frontend field names to backend field names
        const backendKey = key === 'readAt' ? 'read_at' :
          key === 'conversationId' ? 'conversationId' :
            key === 'businessAccountId' ? 'businessAccountId' :
              key === 'contactId' ? 'contactId' :
                key === 'messageType' ? 'messageType' :
                  key === 'isInbound' ? 'isInbound' :
                    key;
        params.append(backendKey, String(value));
      }
    });

    const endpoint = `${API_ENDPOINTS.MESSAGES}${params.toString() ? `?${params.toString()}` : ''}`;
    return apiClient.get<Message[]>(endpoint);
  },

  /**
   * Get a single message by ID
   */
  async getById(id: string) {
    return apiClient.get<Message>(`${API_ENDPOINTS.MESSAGES}/${id}`);
  },

  /**
   * Create a new message
   */
  async create(data: CreateMessageData) {
    return apiClient.post<Message>(API_ENDPOINTS.MESSAGES, data);
  },

  /**
   * React to a message
   */
  async reactToMessage(messageId: string, body: { emoji: string; conversationId: string }) {
    return apiClient.post(`${API_ENDPOINTS.MESSAGES}/${messageId}/react`, body);
  },

  /**
   * Remove reaction from a message
   */
  async removeReaction(messageId: string, body: { conversationId: string }) {
    return apiClient.delete(`${API_ENDPOINTS.MESSAGES}/${messageId}/react`, { body });
  },

  /**
   * Get messages by conversation
   */
  async getByConversation(conversationId: string, filters: Omit<MessageFilters, 'conversationId'> = {}) {
    return this.getAll({ ...filters, conversationId: conversationId });
  },

  /**
   * Get last message for a conversation
   */
  async getLastMessage(conversationId: string) {
    const response = await this.getByConversation(conversationId, {
      orderBy: 'created_at',
      ascending: false,
      limit: 1,
    });
    return {
      ...response,
      data: response.data?.[0] || null,
    };
  },

  /**
   * Mark multiple messages as read (batch operation)
   */
  async markMultipleAsRead(ids: string[]) {
    if (ids.length === 0) {
      return { success: true, data: { updatedCount: 0 } };
    }
    return await apiClient.post<{ success: boolean; message: string; updatedCount: number }>(
      `${API_ENDPOINTS.MESSAGES}/batch/mark-read`,
      { messageIds: ids }
    );
  },

  /**
   * Mark all messages in a conversation as read
   */
  /**
   * Show the customer "typing…" on WhatsApp while the agent composes.
   * Fire-and-forget: throttled to ~once per 20s by the caller; backend always
   * 200s even when there's no inbound message to anchor on.
   */
  async sendTypingIndicator(conversationId: string, businessAccountId: string) {
    return await apiClient.post<{ success: boolean }>(
      `${API_ENDPOINTS.MESSAGES}/conversation/${conversationId}/typing`,
      { businessAccountId }
    )
  },

  async markConversationAsRead(conversationId: string, businessAccountId: string) {
    try {
      return await apiClient.post<{ success: boolean; message: string; updatedCount: number }>(
        `${API_ENDPOINTS.MESSAGES}/conversation/${conversationId}/mark-read`,
        { businessAccountId }
      );
    } catch (error) {
      // Fallback: get unread messages and mark them individually
      const response = await this.getAll({
        businessAccountId,
        conversationId,
        isInbound: true,
        readAt: null
      });

      const unreadMessages = response.data || [];
      if (unreadMessages.length > 0) {
        return await this.markMultipleAsRead(unreadMessages.map((msg: any) => msg.id));
      }

      return { success: true, data: { updatedCount: 0 } };
    }
  },

  /**
   * Get unread count for a conversation
   */
  async getUnreadCount(conversationId: string) {
    const response = await this.getByConversation(conversationId, {
      isInbound: true,
      status: 'DELIVERED',
    });
    return response.data?.length || 0;
  },

  /**
   * Star a message for the current user. Backend stores this per-user so a
   * "Starred" panel can render the same rows across devices.
   */
  async star(
    messageId: string,
    body: {
      conversationId?: string;
      createdAt?: string;
      content?: string;
      messageType?: string;
      mediaUrl?: string;
      mediaType?: string;
      mediaFilename?: string;
      platform?: string;
      contactName?: string;
      isInbound?: boolean;
      whatsappMessageId?: string;
      contextMessageId?: string;
      metadata?: any;
    } = {},
  ) {
    return apiClient.post(`${API_ENDPOINTS.MESSAGES}/${messageId}/star`, body);
  },

  /**
   * Unstar a message for the current user.
   */
  async unstar(messageId: string) {
    return apiClient.delete(`${API_ENDPOINTS.MESSAGES}/${messageId}/star`);
  },

  /**
   * Get all starred messages for the current user.
   */
  async getStarred(params: { limit?: number; pageToken?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.append('limit', String(params.limit));
    if (params.pageToken) qs.append('pageToken', params.pageToken);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiClient.get(`${API_ENDPOINTS.MESSAGES}/starred${suffix}`);
  },

  /**
   * Get the full Message rows for the current user's starred messages within
   * a single conversation. Used when the global Starred filter is active and
   * the user opens a conversation whose starred items may be older than the
   * loaded message page.
   */
  async getStarredForConversation(conversationId: string) {
    return apiClient.get(`${API_ENDPOINTS.MESSAGES}/conversation/${conversationId}/starred`);
  },

  /**
   * Forward a message to another conversation. Backend clones the media/text
   * and sends it out to the target conversation's WhatsApp number.
   */
  async forward(body: {
    messageId: string;
    targetConversationId: string;
    sourceConversationId?: string;
    createdAt?: string;
  }) {
    return apiClient.post(`${API_ENDPOINTS.MESSAGES}/forward`, body);
  },
};
